import React from 'react';
import { injectIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0
  },
  toggleGroup: {
    position: 'relative',
    backgroundColor: '#f3f5fb',
    borderRadius: '8px !important',
    padding: 6,
    zIndex: 0
  },
  toggleButton: {
    border: 'none',
    borderRadius: '8px !important',
    padding: '0 16px',
    fontWeight: 500,
    fontSize: 14,
    height: 36,
    minWidth: 110,
    color: '#616161',
    textTransform: 'none',
    zIndex: 2,
    backgroundColor: 'transparent',
    transition: 'color 0.25s ease !important',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(57, 102, 248, 0.04)'
    }
  },
  selected: {
    color: '#ffffff !important',
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important'
    }
  },
  slidingIndicator: {
    position: 'absolute',
    top: 6,
    bottom: 6,
    left: 6,
    width: 'calc(50% - 6px)',
    backgroundColor: '#3966f8',
    borderRadius: 8,
    boxShadow: '0 4px 10px rgba(57, 102, 248, 0.2)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1
  }
}));

export type HotTopicMode = 'existing' | 'studio';

interface HotTopicModeToggleProps {
  intl: any;
  mode: HotTopicMode;
  onChange: (mode: HotTopicMode) => void;
}

const ANIMATION_MS = 350;

const HotTopicModeToggle: React.FC<HotTopicModeToggleProps> = ({ intl, mode, onChange }) => {
  const { classes } = useStyles();
  // Local value so the sliding indicator can animate before navigation unmounts this view.
  const [localMode, setLocalMode] = React.useState<HotTopicMode>(mode);
  const pendingTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    setLocalMode(mode);
  }, [mode]);

  React.useEffect(() => {
    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
    };
  }, []);

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newMode: HotTopicMode | null) => {
    if (newMode === null || newMode === localMode) {
      return;
    }
    setLocalMode(newMode);
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
    }
    pendingTimerRef.current = setTimeout(() => {
      onChange(newMode);
    }, ANIMATION_MS);
  };

  const getIndex = (val: HotTopicMode) => (val === 'studio' ? 1 : 0);

  return (
    <div className={classes.root}>
      <ToggleButtonGroup
        value={localMode}
        exclusive
        onChange={handleChange}
        aria-label="hot topic mode toggle"
        classes={{ root: classes.toggleGroup }}
      >
        <div
          className={classes.slidingIndicator}
          style={{
            transform: `translateX(${getIndex(localMode) * 100}%)`
          }}
        />
        <ToggleButton value="existing" classes={{ root: classes.toggleButton, selected: classes.selected }}>
          {intl.formatMessage({ id: 'hot-topic-mode-existing' })}
        </ToggleButton>
        <ToggleButton value="studio" classes={{ root: classes.toggleButton, selected: classes.selected }}>
          {intl.formatMessage({ id: 'hot-topic-mode-studio' })}
        </ToggleButton>
      </ToggleButtonGroup>
    </div>
  );
};

export default injectIntl(HotTopicModeToggle);
